import { useEffect, useState } from 'react'
import { descargar } from '../api/cliente'
import { parametrosAlcance, reportes } from '../api/servicios'
import type { AlcanceReporte } from '../api/servicios'
import type { AlcancesDisponibles } from '../api/tipos'
import { cantidad as formatearCantidad, fechaHora, dinero } from '../api/formato'
import { Estado } from '../componentes/Estado'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import {
    finDelDia as finDelDiaEnZona,
    hoy,
    inicioDelDia as inicioDelDiaEnZona,
    primerDiaDelMes,
} from '../api/zona'
import './listados.css'

type Vista = 'ventas' | 'productos' | 'promociones' | 'inventario' | 'credito' | 'arqueos'

const VISTAS: { valor: Vista; texto: string }[] = [
    { valor: 'ventas', texto: 'Ventas' },
    { valor: 'productos', texto: 'Más vendidos' },
    { valor: 'promociones', texto: 'Promociones' },
    { valor: 'inventario', texto: 'Inventario' },
    { valor: 'credito', texto: 'Crédito' },
    { valor: 'arqueos', texto: 'Arqueos' },
]

/*
 * Los campos de fecha entregan solo el dia; el backend espera un instante. El
 * dia se abre y se cierra en la **zona del local**, no en la del equipo: de
 * otro modo un cierre de las 23:40 se cuenta en el dia siguiente o se pierde,
 * y el mismo reporte da distinto segun desde donde se mire.
 *
 * El calculo vive en api/zona.ts porque hay dias en que la medianoche no
 * existe y no alcanza con restar horas.
 */
function inicioDelDia(valor: string): string {
    return inicioDelDiaEnZona(valor).toISOString()
}

function finDelDia(valor: string): string {
    return finDelDiaEnZona(valor).toISOString()
}

export function Reportes() {
    const [vista, setVista] = useState<Vista>('ventas')
    const [desde, setDesde] = useState(primerDiaDelMes())
    const [hasta, setHasta] = useState(hoy())

    // Sobre que local se calcula. Vacio = el propio, que es el caso corriente
    // y el unico que existe mientras haya una sola sucursal.
    const [alcance, setAlcance] = useState<AlcanceReporte>({})
    const [alcances, setAlcances] = useState<AlcancesDisponibles | null>(null)

    useEffect(() => {
        let vigente = true

        reportes
            .alcances()
            .then((a) => {
                if (vigente) setAlcances(a)
            })
            .catch(() => {
                // Sin la lista, cada quien ve su local: es lo que pasaba antes
                // de que existiera el selector, no un motivo para no mostrar
                // el reporte.
            })

        return () => {
            vigente = false
        }
    }, [])

    // Con un solo local el selector no aporta nada y se omite.
    const hayDondeElegir = (alcances?.puedeVerOtras ?? false) && (alcances?.sucursales.length ?? 0) > 1

    const valorAlcance = alcance.consolidado
        ? 'todas'
        : alcance.sucursalId !== undefined
          ? String(alcance.sucursalId)
          : 'propia'

    function elegirAlcance(valor: string) {
        if (valor === 'todas') {
            setAlcance({ consolidado: true })
        } else if (valor === 'propia') {
            setAlcance({})
        } else {
            setAlcance({ sucursalId: Number(valor) })
        }
    }

    const conRango =
        vista === 'ventas' ||
        vista === 'productos' ||
        vista === 'promociones' ||
        vista === 'arqueos'

    return (
        <div className="pagina-listado columna g4">
            <h1>Reportes</h1>

            <div className="pestanias" role="tablist">
                {VISTAS.map((v) => (
                    <button
                        key={v.valor}
                        type="button"
                        role="tab"
                        className="pestania"
                        aria-selected={vista === v.valor}
                        onClick={() => setVista(v.valor)}
                    >
                        {v.texto}
                    </button>
                ))}
            </div>

            {(conRango || hayDondeElegir) && (
                <div className="filtros">
                    {hayDondeElegir && (
                        <div>
                            <label className="etiqueta" htmlFor="alcance">
                                Local
                            </label>
                            <select
                                id="alcance"
                                className="campo"
                                value={valorAlcance}
                                onChange={(e) => elegirAlcance(e.target.value)}
                            >
                                <option value="propia">El mío</option>
                                {alcances?.sucursales.map((s) => (
                                    <option key={s.sucursalId} value={s.sucursalId}>
                                        {s.nombre}
                                    </option>
                                ))}
                                <option value="todas">Todos (consolidado)</option>
                            </select>
                        </div>
                    )}

                    {conRango && (
                    <>
                    <div>
                        <label className="etiqueta" htmlFor="desde">
                            Desde
                        </label>
                        <input
                            id="desde"
                            className="campo"
                            type="date"
                            max={hasta}
                            value={desde}
                            onChange={(e) => setDesde(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="hasta">
                            Hasta
                        </label>
                        <input
                            id="hasta"
                            className="campo"
                            type="date"
                            min={desde}
                            value={hasta}
                            onChange={(e) => setHasta(e.target.value)}
                        />
                    </div>
                    </>
                    )}
                </div>
            )}

            {vista === 'ventas' && <Ventas desde={desde} hasta={hasta} alcance={alcance} />}
            {vista === 'productos' && <MasVendidos desde={desde} hasta={hasta} alcance={alcance} />}
            {vista === 'promociones' && <Promociones desde={desde} hasta={hasta} alcance={alcance} />}
            {vista === 'inventario' && <InventarioValorizado alcance={alcance} />}
            {vista === 'credito' && <CreditoPendiente />}
            {vista === 'arqueos' && <Arqueos desde={desde} hasta={hasta} alcance={alcance} />}
        </div>
    )
}

interface PropsRango {
    desde: string
    hasta: string
    alcance: AlcanceReporte
}

/**
 * Los parametros de alcance que entienden los endpoints CSV. Se arman aparte
 * porque `descargar` no pasa por los servicios: pide la ruta en crudo.
 */
function alcanceCsv(alcance: AlcanceReporte): Record<string, number | boolean | undefined> {
    return parametrosAlcance(alcance)
}

/** Boton de descarga con su propio estado: una descarga puede fallar sola. */
function BotonCsv({
    ruta,
    nombre,
    parametros,
}: {
    ruta: string
    nombre: string
    parametros?: Record<string, string | number | boolean | undefined>
}) {
    const [error, setError] = useState<string | null>(null)
    const [bajando, setBajando] = useState(false)

    async function bajar() {
        setError(null)
        setBajando(true)

        try {
            await descargar(ruta, nombre, parametros)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setBajando(false)
        }
    }

    return (
        <div className="columna g2">
            <button type="button" className="boton" onClick={bajar} disabled={bajando}>
                {bajando ? 'Generando…' : 'Descargar CSV'}
            </button>

            {error && (
                <div className="aviso aviso-error" role="alert">
                    {error}
                </div>
            )}
        </div>
    )
}

function Cifra({ titulo, valor }: { titulo: string; valor: string }) {
    return (
        <div className="tarjeta-cifra">
            <span className="etiqueta">{titulo}</span>
            <span className="cifra">{valor}</span>
        </div>
    )
}

/* -------------------------------------------------------------------------- */

function Ventas({ desde, hasta, alcance }: PropsRango) {
    const reporte = usarCargar(
        () => reportes.ventas(inicioDelDia(desde), finDelDia(hasta), alcance),
        [desde, hasta, alcance],
    )

    return (
        <section className="panel columna g4">
            <Estado cargando={reporte.cargando} error={reporte.error} alReintentar={reporte.recargar}>
                {reporte.datos && (
                    <>
                        <div className="tarjetas-cifra">
                            <Cifra titulo="Ventas" valor={String(reporte.datos.cantidadVentas)} />
                            <Cifra titulo="Total vendido" valor={dinero(reporte.datos.totalVendido)} />
                            <Cifra titulo="Ticket promedio" valor={dinero(reporte.datos.ticketPromedio)} />
                            <Cifra titulo="Descuentos" valor={dinero(reporte.datos.totalDescuentos)} />
                            <Cifra titulo="Devuelto" valor={dinero(reporte.datos.totalDevuelto)} />
                        </div>

                        <div className="dos-paneles">
                            <section className="columna g2">
                                <h2>Por medio de pago</h2>
                                <div className="tabla-envoltura">
                                    <table className="tabla">
                                        <thead>
                                            <tr>
                                                <th>Medio</th>
                                                <th className="derecha">Operaciones</th>
                                                <th className="derecha">Importe</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reporte.datos.porMedioPago.map((m) => (
                                                <tr key={m.medioPago}>
                                                    <td>{m.medioPago}</td>
                                                    <td className="derecha numero">
                                                        {m.cantidadOperaciones}
                                                    </td>
                                                    <td className="derecha numero">
                                                        {dinero(m.importe)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <section className="columna g2">
                                <h2>Por cajero</h2>
                                <div className="tabla-envoltura">
                                    <table className="tabla">
                                        <thead>
                                            <tr>
                                                <th>Cajero</th>
                                                <th className="derecha">Ventas</th>
                                                <th className="derecha">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reporte.datos.porCajero.map((c) => (
                                                <tr key={c.usuarioId}>
                                                    <td>{c.nombreUsuario}</td>
                                                    <td className="derecha numero">
                                                        {c.cantidadVentas}
                                                    </td>
                                                    <td className="derecha numero">{dinero(c.total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </div>

                        <section className="columna g2">
                            <h2>Por día</h2>
                            <div className="tabla-envoltura">
                                <table className="tabla">
                                    <thead>
                                        <tr>
                                            <th>Día</th>
                                            <th className="derecha">Ventas</th>
                                            <th className="derecha">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reporte.datos.porDia.map((d) => (
                                            <tr key={d.fecha}>
                                                <td className="numero">{d.fecha}</td>
                                                <td className="derecha numero">{d.cantidadVentas}</td>
                                                <td className="derecha numero">{dinero(d.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>
                )}
            </Estado>

            <BotonCsv
                ruta="/reportes/ventas.csv"
                nombre={`ventas-${desde}-a-${hasta}.csv`}
                parametros={{
                    desde: inicioDelDia(desde),
                    hasta: finDelDia(hasta),
                    ...alcanceCsv(alcance),
                }}
            />
        </section>
    )
}

/* -------------------------------------------------------------------------- */

function MasVendidos({ desde, hasta, alcance }: PropsRango) {
    const [porImporte, setPorImporte] = useState(true)

    const productos = usarCargar(
        () =>
            reportes.productosMasVendidos(
                inicioDelDia(desde), finDelDia(hasta), porImporte, alcance),
        [desde, hasta, porImporte, alcance],
    )

    return (
        <section className="panel columna g3">
            <div className="grupo-opciones">
                <button
                    type="button"
                    className={`boton${porImporte ? ' boton-primario' : ''}`}
                    aria-pressed={porImporte}
                    onClick={() => setPorImporte(true)}
                >
                    Por importe
                </button>
                <button
                    type="button"
                    className={`boton${!porImporte ? ' boton-primario' : ''}`}
                    aria-pressed={!porImporte}
                    onClick={() => setPorImporte(false)}
                >
                    Por cantidad
                </button>
            </div>

            <Estado
                cargando={productos.cargando}
                error={productos.error}
                alReintentar={productos.recargar}
                vacio={(productos.datos ?? []).length === 0}
                mensajeVacio="No hubo ventas en el período."
            >
                <div className="tabla-envoltura">
                    <table className="tabla">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="derecha">Cantidad</th>
                                <th className="derecha">Importe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(productos.datos ?? []).map((p) => (
                                <tr key={p.productoId}>
                                    <td>
                                        <div className="columna">
                                            <span>{p.descripcion}</span>
                                            <span className="tenue diminuto numero">{p.sku}</span>
                                        </div>
                                    </td>
                                    <td className="derecha numero">
                                        {formatearCantidad(p.cantidad, 3)}
                                    </td>
                                    <td className="derecha numero">{dinero(p.importe)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Estado>
        </section>
    )
}

/* -------------------------------------------------------------------------- */

function InventarioValorizado({ alcance }: { alcance: AlcanceReporte }) {
    const reporte = usarCargar(() => reportes.inventarioValorizado(alcance), [alcance])

    return (
        <section className="panel columna g3">
            <Estado cargando={reporte.cargando} error={reporte.error} alReintentar={reporte.recargar}>
                {reporte.datos && (
                    <>
                        <div className="tarjetas-cifra">
                            <Cifra titulo="Valor del inventario" valor={dinero(reporte.datos.valorTotal)} />
                            <Cifra titulo="Productos" valor={String(reporte.datos.productos.length)} />
                        </div>

                        <div className="tabla-envoltura">
                            <table className="tabla">
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th className="derecha">Cantidad</th>
                                        <th className="derecha oculto-angosto">Último costo</th>
                                        <th className="derecha">Valorizado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reporte.datos.productos.map((p) => (
                                        <tr key={p.productoId}>
                                            <td>
                                                <div className="columna">
                                                    <span>{p.nombre}</span>
                                                    <span className="tenue diminuto numero">
                                                        {p.sku}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="derecha numero">
                                                {formatearCantidad(p.cantidad, 3)}
                                            </td>
                                            <td className="derecha numero oculto-angosto">
                                                {dinero(p.ultimoCosto)}
                                            </td>
                                            <td className="derecha numero">{dinero(p.valorizado)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Estado>

            <BotonCsv
                ruta="/reportes/inventario-valorizado.csv"
                nombre="inventario-valorizado.csv"
                parametros={alcanceCsv(alcance)}
            />
        </section>
    )
}

/* -------------------------------------------------------------------------- */

function CreditoPendiente() {
    const pendientes = usarCargar(() => reportes.creditoPendiente(), [])

    const total = (pendientes.datos ?? []).reduce((suma, c) => suma + c.saldo, 0)

    return (
        <section className="panel columna g3">
            <Estado
                cargando={pendientes.cargando}
                error={pendientes.error}
                alReintentar={pendientes.recargar}
                vacio={(pendientes.datos ?? []).length === 0}
                mensajeVacio="Ningún cliente tiene deuda pendiente."
            >
                <>
                    <div className="tarjetas-cifra">
                        <Cifra titulo="Deuda total" valor={dinero(total)} />
                        <Cifra titulo="Clientes" valor={String((pendientes.datos ?? []).length)} />
                    </div>

                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th className="oculto-angosto">Teléfono</th>
                                    <th className="derecha">Antigüedad</th>
                                    <th className="derecha">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(pendientes.datos ?? []).map((c) => (
                                    <tr key={c.clienteId}>
                                        <td>
                                            <div className="columna">
                                                <span>{c.cliente}</span>
                                                <span className="tenue diminuto numero">
                                                    {c.documento ?? 'sin RUT'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto numero chico">
                                            {c.telefono ?? '—'}
                                        </td>
                                        <td className="derecha numero">{c.diasAntiguedad} días</td>
                                        <td className="derecha numero">{dinero(c.saldo)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            </Estado>

            <BotonCsv ruta="/reportes/credito-pendiente.csv" nombre="credito-pendiente.csv" />
        </section>
    )
}

/* -------------------------------------------------------------------------- */

function Arqueos({ desde, hasta, alcance }: PropsRango) {
    const [soloConDiferencia, setSoloConDiferencia] = useState(false)

    const arqueos = usarCargar(
        () =>
            reportes.arqueos(
                inicioDelDia(desde), finDelDia(hasta), soloConDiferencia, alcance),
        [desde, hasta, soloConDiferencia, alcance],
    )

    return (
        <section className="panel columna g3">
            <label className="fila g2 interruptor">
                <input
                    type="checkbox"
                    checked={soloConDiferencia}
                    onChange={(e) => setSoloConDiferencia(e.target.checked)}
                />
                <span>Solo cierres con diferencia</span>
            </label>

            <Estado
                cargando={arqueos.cargando}
                error={arqueos.error}
                alReintentar={arqueos.recargar}
                vacio={(arqueos.datos ?? []).length === 0}
                mensajeVacio={
                    soloConDiferencia
                        ? 'Ningún cierre del período tuvo diferencia.'
                        : 'No hubo cierres de caja en el período.'
                }
            >
                <div className="tabla-envoltura">
                    <table className="tabla">
                        <thead>
                            <tr>
                                <th>Caja</th>
                                <th className="oculto-angosto">Cerrada</th>
                                <th className="derecha oculto-angosto">Esperado</th>
                                <th className="derecha oculto-angosto">Contado</th>
                                <th className="derecha">Diferencia</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(arqueos.datos ?? []).map((a) => (
                                <tr key={a.sesionCajaId}>
                                    <td>
                                        <div className="columna">
                                            <span>
                                                {/* Consolidado, "CAJA1" no dice
                                                    de que local: va delante. */}
                                                {alcance.consolidado && `${a.sucursal} · `}
                                                {a.caja}
                                            </span>
                                            <span className="tenue diminuto">{a.cajero}</span>
                                        </div>
                                    </td>
                                    <td className="oculto-angosto tenue chico">
                                        {fechaHora(a.cerradaEn)}
                                    </td>
                                    <td className="derecha numero oculto-angosto">
                                        {dinero(a.efectivoEsperado)}
                                    </td>
                                    <td className="derecha numero oculto-angosto">
                                        {dinero(a.efectivoContado)}
                                    </td>
                                    <td
                                        className={`derecha numero ${
                                            (a.diferencia ?? 0) === 0
                                                ? ''
                                                : (a.diferencia ?? 0) > 0
                                                  ? 'monto-suma'
                                                  : 'monto-resta'
                                        }`}
                                    >
                                        {dinero(a.diferencia)}
                                        {a.observacionCierre && (
                                            <div className="tenue diminuto">
                                                {a.observacionCierre}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Estado>
        </section>
    )
}

/* -------------------------------------------------------------------------- */

/**
 * Lo que costo cada promocion en el periodo.
 *
 * Es el unico lugar donde ese costo se ve: el arqueo no lo delata, porque el
 * efectivo esperado sale de los pagos ya descontados y la caja cuadra igual
 * aunque una promocion este mal cargada.
 */
function Promociones({ desde, hasta, alcance }: PropsRango) {
    const reporte = usarCargar(
        () => reportes.promociones(inicioDelDia(desde), finDelDia(hasta), alcance),
        [desde, hasta],
    )

    const filas = reporte.datos ?? []
    const total = filas.reduce((suma, p) => suma + p.totalDescontado, 0)

    return (
        <section className="panel columna g3">
            <Estado
                cargando={reporte.cargando}
                error={reporte.error}
                alReintentar={reporte.recargar}
                vacio={filas.length === 0}
                mensajeVacio="Ninguna promoción se aplicó en este período."
            >
                <>
                    <div className="tarjetas-cifra">
                        <Cifra titulo="Dejado de cobrar" valor={dinero(total)} />
                        <Cifra titulo="Promociones usadas" valor={String(filas.length)} />
                    </div>

                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Promoción</th>
                                    <th className="derecha">Líneas</th>
                                    <th className="derecha oculto-angosto">Boletas</th>
                                    <th className="derecha">Dejado de cobrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filas.map((p) => (
                                    <tr key={p.promocionId}>
                                        <td>{p.nombre}</td>
                                        <td className="derecha numero">{p.vecesAplicada}</td>
                                        <td className="derecha numero oculto-angosto">
                                            {p.cantidadVentas}
                                        </td>
                                        <td className="derecha numero">
                                            {dinero(p.totalDescontado)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            </Estado>

            <BotonCsv
                ruta="/reportes/promociones.csv"
                nombre={`promociones-${desde}-a-${hasta}.csv`}
                parametros={{
                    desde: inicioDelDia(desde),
                    hasta: finDelDia(hasta),
                    ...alcanceCsv(alcance),
                }}
            />
        </section>
    )
}
